<template>
  <div class="dashboard-container">
    <!-- Alerts Section -->
    <div class="alerts-container">
      <div v-if="subscriptionStatus !== 'active'" class="alert" :class="subscriptionStatus === 'expiring' ? 'alert-warning' : 'alert-danger'">
        <div class="d-flex justify-content-between align-items-center">
          <span>Your subscription {{ subscriptionStatus }} on {{ formatDate(subscription.to) }}</span>
          <button v-if="subscription.package.name === 'Trial'" class="btn btn-sm btn-outline-light ms-3" @click="$router.push('upgrade')">
            Upgrade Now
          </button>
          <button v-else class="btn btn-sm btn-outline-light ms-3" @click="subscriptionModal = true">
            Renew Subscription
          </button>
        </div>
      </div>
      <div v-if="subscription.package.employees <= employeesNo" class="alert alert-info">
        <div class="d-flex justify-content-between align-items-center">
          <span>Your subscription has reached the limit of {{ subscription.package.employees }} employees.</span>
          <button class="btn btn-sm btn-outline-light ms-3" @click="$router.push('upgrade')">
            Upgrade Plan
          </button>
        </div>
      </div>
    </div>

    <!-- Header -->
    <div class="dashboard-header mb-4">
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <h1 class="mb-1">Welcome back, {{ profile.first_name }}!</h1>
          <p class="text-muted mb-0">Here's what's happening with your store today</p>
        </div>
        <div class="date-display">
          {{ new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) }}
        </div>
      </div>
    </div>

    <!-- Quick Actions -->
    <div class="quick-actions mb-5">
      <h5 class="section-title mb-3">Quick Actions</h5>
      <div class="row g-3">
        <div v-if="profile.role !== 'user'" class="col-6 col-sm-4 col-lg-3" @click="$router.push('/add-product')">
          <div class="quick-action-card bg-white text-white">
            <div class="icon-wrapper bg-primary">
              <vue-feather type="plus" size="24" stroke="#ffffff"></vue-feather>
            </div>
            <div class="action-text">Add Product</div>
            <div class="text-muted small mt-1">Add a new product to your store</div>
          </div>
        </div>
        <div v-if="profile.role !== 'user'" class="col-6 col-sm-4 col-lg-3" @click="$router.push('/add-supplier')">
          <div class="quick-action-card bg-white text-white">
            <div class="icon-wrapper bg-success">
              <vue-feather type="truck" size="24" stroke="#ffffff"></vue-feather>
            </div>
            <div class="action-text">Add Supplier</div>
            <div class="text-muted small mt-1">Add a new supplier to your store</div>
          </div>
        </div>
        <div class="col-6 col-sm-4 col-lg-3" @click="$router.push('/sales')">
          <div class="quick-action-card bg-white text-white">
            <div class="icon-wrapper bg-info">
              <vue-feather type="dollar-sign" size="24" stroke="#ffffff"></vue-feather>
            </div>
            <div class="action-text">View Sales</div>
            <div class="text-muted small mt-1">View your sales history</div>
          </div>
        </div>
        <div class="col-6 col-sm-4 col-lg-3" @click="$router.push('/transfers')">
          <div class="quick-action-card bg-white text-dark">
            <div class="icon-wrapper bg-warning">
              <vue-feather type="refresh-cw" size="24" stroke="#ffffff"></vue-feather>
            </div>
            <div class="action-text">Manage Transfers</div>
            <div class="text-muted small mt-1">Manage your product transfers</div>
          </div>
        </div>
      </div>
    </div>
    <!-- Stats Overview -->
    <div class="stats-overview mb-5">
      <h5 class="section-title mb-4">Store Overview</h5>
      <div class="row g-4">
        <div class="col-xxl-3 col-xl-4 col-md-6" v-for="(item, index) in infoCols" :key="index">
          <div class="stats-card fade-in" :style="{'animation-delay': `${index * 0.1}s`}">
            <div class="d-flex align-items-center">
              <div class="me-3">
                <div class="icon" :class="`bg-${item.bg}-light`">
                  <vue-feather :type="item.icon" :stroke="item.color" size="24"></vue-feather>
                </div>
              </div>
              <div>
                <div class="stat-value" :class="`text-${item.bg}`">
                  {{ item.value }}
                </div>
                <div class="stat-label">
                  {{ item.title }}
                </div>
                <div class="text-muted small mt-1">
                  {{ analyticsText(item.desc) }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Product Performance -->
    <div class="product-performance">
      <div class="row g-4">
        <!-- Most Sold Products -->
        <div class="col-lg-6">
          <div class="table-card h-100">
            <div class="card-header d-flex align-items-center justify-content-between">
              <h5 class="mb-0">Most Sold Products</h5>
              <div class="d-flex align-items-center">
                <select class="form-select form-select-sm me-3" v-model="popularityCount" style="max-width: 120px;" data="">
                  <option v-for="count in [5, 10, 25, 50, 100]"
                          :key="count"
                          :value="count"
                          :selected="popularityCount === count">
                    {{ count }} Products
                  </option>
                </select>
                <button class="btn btn-outline-primary" @click="exportPopularityToPDF">
                  <i class="bi bi-file-pdf me-1"></i> Export PDF
                </button>
              </div>
            </div>
            <div class="table-responsive">
              <table class="table table-hover align-middle">
                <thead>
                  <tr>
                    <th>PRODUCT</th>
                    <th class="text-end">SOLD</th>
                  </tr>
                </thead>
                <tbody v-if="!loading">
                  <tr v-for="(product, index) in popularity" :key="index" class="product-row">
                    <td>
                      <div class="d-flex align-items-center">
                        <div class="product-avatar me-2">
                          <span class="avatar-text">{{ product.name.charAt(0).toUpperCase() }}</span>
                        </div>
                        <div>
                          <div class="fw-medium">{{ product.name }}</div>
                          <small class="text-muted">{{ product.sku }}</small>
                        </div>
                      </div>
                    </td>
                    <td class="text-end">
                      <div class="fw-medium">{{ product.quantity }}</div>
                      <small class="text-muted">units</small>
                    </td>
                  </tr>
                </tbody>
                <tbody v-else>
                  <tr>
                    <td colspan="2" class="text-center py-4">
                      <Spinner spinner-class="mb-2" />
                      <div>Loading products...</div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Most Profitable Products -->
        <div class="col-lg-6">
          <div class="table-card h-100">
            <div class="card-header d-flex align-items-center justify-content-between">
              <h5 class="mb-0">Most Profitable Products</h5>
              <div class="d-flex align-items-center">
                <select class="form-select form-select-sm me-3" v-model="profitabilityCount" style="max-width: 120px;" data="">
                  <option v-for="count in [5, 10, 25, 50, 100]"
                          :key="count"
                          :value="count"
                          :selected="profitabilityCount === count">
                    {{ count }} Products
                  </option>
                </select>
                <button class="btn btn-outline-primary" @click="exportProfitabilityToPDF">
                  <i class="bi bi-file-pdf me-1"></i> Export PDF
                </button>
              </div>
            </div>
            <div class="table-responsive">
              <table class="table table-hover align-middle">
                <thead>
                  <tr>
                    <th>PRODUCT</th>
                    <th class="text-end">SOLD</th>
                    <th class="text-end">PROFIT</th>
                  </tr>
                </thead>
                <tbody v-if="!loading">
                  <tr v-for="(product, index) in profitability" :key="index" class="product-row">
                    <td>
                      <div class="d-flex align-items-center">
                        <div class="product-avatar me-2" :class="`bg-${['success', 'info', 'warning', 'danger'][index % 4]}-light`">
                          <span class="avatar-text">{{ product.name.charAt(0).toUpperCase() }}</span>
                        </div>
                        <div>
                          <div class="fw-medium">{{ product.name }}</div>
                          <small class="text-muted">{{ product.sku }}</small>
                        </div>
                      </div>
                    </td>
                    <td class="text-end">
                      <div class="fw-medium">{{ product.quantity }}</div>
                      <small class="text-muted">units</small>
                    </td>
                    <td class="text-end">
                      <div class="fw-medium text-success">{{ formatNumber(product.profit) }}</div>
                      <small class="text-muted">KES</small>
                    </td>
                  </tr>
                </tbody>
                <tbody v-else>
                  <tr>
                    <td colspan="3" class="text-center py-4">
                      <Spinner spinner-class="mb-2" />
                      <div>Loading products...</div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
      </div>

      <!-- Renew Subscription Modal -->
      <Modal :open="subscriptionModal" @closed="subscriptionModal = false" dialog-size="modal-md">
        <div class="modal-header">
          <h5 class="modal-title m-0">Renew Subscription</h5>
          <button @click="subscriptionModal = false" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
          </button>
        </div>
        <div class="modal-body">
          <div role="alert" class="alert alert-info">
            <div class="content text-center">
              Get one month free! By subscribing for 12 months.
            </div>
          </div>
          <br>
          <span style="display:block; text-align: center">
          <img src="https://intasend-prod-static.s3.amazonaws.com/img/trust-badges/intasend-trust-badge-with-mpesa-hr-dark.png" width="100%" alt="IntaSend">
        </span>
          <hr>
          <form @submit.prevent="">
            <div class="row">
              <div class="form-group col-md-12">
                <label for="duration" class="form-label fw-bold h5 mt-3">Months:</label>
                <select data="" class="form-select form-select-lg" v-model="duration">
                  <option value="1" selected>1 Month</option>
                  <option value="6">6 Months</option>
                  <option value="12">12 Months</option>
                </select>
              </div>
              <div class="form-group col-md-12">
                <label for="price" class="form-label h5 mt-3"><strong>Price:</strong> {{ formatNumber(subscription.package.price) }}</label>
                <hr>
                <div class="float-end">
                  <h5 class="text-muted fst-italic">Total: {{ formatNumber(duration * subscription.package.price) }}</h5>
                  <h5 class="text-muted fst-italic">Discount: {{ formatNumber(duration === "12" ? subscription.package.price : 0) }}</h5>
                </div>
              </div>
              <div class="form-group col-md-12">
                <label for="total" class="form-label fw-bold h4 mt-3 float-end">Total: {{ formatNumber(amount) }}</label>
              </div>
            </div>
          </form>
        </div>
        <div class="modal-footer border-1 border-info border-opacity-75 mt-3">
          <button type="button" class="btn btn-dark text-uppercase me-3" data-dismiss="modal" @click="subscriptionModal = false">Cancel</button>
          <button type="button" class="btn btn-primary text-uppercase" @click="renewSubscription">Checkout</button>
        </div>
      </Modal>
      <!-- Renew Subscription Modal End -->
</template>

<script>
    import analyticsService from "@/api/analyticsService";
    import BarChart from "@/components/charts/BarChart.vue";
    import formatNumber from "../utils/formatNumber";
    import LineChart from "@/components/charts/LineChart.vue";
    import moment from "moment";
    import formatDate from "../utils/formatDate";
    import {decrypt, encrypt} from "@/utils/crypto";
    import 'intasend-inlinejs-sdk';
    import subscriptionService from "@/api/subscriptionService";
    import notify from "@/utils/notify";
    import organizationService from "@/api/organizationService";
    import exportToPDF from "@/utils/exportToPDF";

    export default {
      name: "Dashboard",
      emits:['confirm', 'cancel'],
      components: {LineChart, BarChart },
      data() {
        return {
          loading: true,
          store: {},
          analytics: {
            data: {
              labels: [],
              datasets: []
            }
          },
          year: new Date().getFullYear(),
          years: [],
          profile: {},
          subscription: {
            package: {}
          },
          popularity: [],
          popularityCount: 10,
          profitability: [],
          profitabilityCount: 10,
          organization: {},
          duration: 1,
          amount: 0,
          subscriptionStatus: '',
          subscriptionModal: false,
          paymentRef: '',
          redirectUrl: process.env.VUE_APP_BASE_URL + 'paymentSuccess',
          employeesNo: 0,
        }
      },

      methods: {
        formatDate,
        formatNumber,
        exportPopularityToPDF() {
          // Prepare information lines
          const info = [
            `Store: ${this.store.name}`,
            `Date: ${new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}`
          ];

          // Prepare table columns
          const tableColumns = ["PRODUCT", "SKU", "SOLD"];

          // Prepare table data
          const tableData = this.popularity.map(product => [
            product.name,
            product.sku,
            `${product.quantity} units`
          ]);

          // Call the exportToPDF utility function
          exportToPDF({
            title: 'Most Sold Products',
            info,
            tableColumns,
            tableData,
            filename: `MostSoldProducts_${this.store.name}_${new Date().toISOString().split('T')[0]}.pdf`,
            formatNumber: (val) => this.formatNumber(val, false)
          });
        },
        exportProfitabilityToPDF() {
          // Prepare information lines
          const info = [
            `Store: ${this.store.name}`,
            `Date: ${new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}`
          ];

          // Prepare table columns
          const tableColumns = ["PRODUCT", "SKU", "SOLD", "PROFIT"];

          // Prepare table data
          const tableData = this.profitability.map(product => [
            product.name,
            product.sku,
            `${product.quantity} units`,
            this.formatNumber(product.profit, false)
          ]);

          // Call the exportToPDF utility function
          exportToPDF({
            title: 'Most Profitable Products',
            info,
            tableColumns,
            tableData,
            filename: `MostProfitableProducts_${this.store.name}_${new Date().toISOString().split('T')[0]}.pdf`,
            formatNumber: (val) => this.formatNumber(val, false)
          });
        },
        getAnalytics: async function () {
          this.analytics = await analyticsService.searchStoreAnalytics({store_id: this.store.id})
          this.popularity = await analyticsService.getProductPopularity(this.popularityCount)
          this.profitability = await analyticsService.getProductProfitability(this.profitabilityCount)
        },
        getSubscription: async function () {
          if (sessionStorage.getItem('paymentSuccess') === 'true') {
            this.subscription = await subscriptionService.getSubscription()
          } else {
            this.subscription = JSON.parse(decrypt(sessionStorage.getItem('subscription')))
          }
        },
        getSubscriptionStatus: function () {
          let today = moment(new Date())
          let to = moment(this.subscription.to)
          let days = to.diff(today, 'days');
          if (days < 0) {
            this.subscriptionStatus = 'expired'
          } else if (days === 0) {
            if (today.format("YYYY-MM-DD") === to.format("YYYY-MM-DD")) {
              this.subscriptionStatus = 'expired'
            } else {
              this.subscriptionStatus = 'expiring'
            }
          } else if (days < 4 && days > 0) {
            this.subscriptionStatus = 'expiring'
          } else {
            this.subscriptionStatus = 'active'
          }
          this.amount = this.subscription.package.price
          sessionStorage.setItem('subscriptionStatus', encrypt(this.subscriptionStatus))
        },
        renewSubscription: async function () {
          let response = await subscriptionService.initPayment({
            organization_id: this.subscription.organization_id,
            package_id: this.subscription.package.id,
            type: 'renew',
            package: this.subscription.package.name,
            duration: this.duration,
            amount: this.amount,
          })
          if (response.status === 201) {
            this.subscriptionModal = false
            this.$refs.payBtn.setAttribute('data-api_ref', response.data.id)
            this.$refs.payBtn.click()
          }
        },
        analyticsText(desc) {
          return desc ? desc : 'Today summary'
        }
      },

      created: async function () {
        this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')))
        await this.getSubscription()
        this.organization = JSON.parse(decrypt(sessionStorage.getItem('organization')))
        this.store = JSON.parse(decrypt(sessionStorage.getItem('store')))
        this.getSubscriptionStatus()
        await this.getAnalytics()
        this.years = this.analytics.years
        this.loading = false
      },

      mounted: async function () {
        new window.IntaSend({
          publicAPIKey: process.env.VUE_APP_INTASEND_API_KEY,
          live: process.env.NODE_ENV !== 'development',
          redirectURL: this.redirectUrl
        })
      },

      computed: {
        infoCols() {
          let products = this.analytics.count ?? 0
          return [
            {
              title:'Sales',
              value:formatNumber(this.analytics.total - this.analytics.discount),
              icon:"dollar-sign",
              color:"dodgerblue",
              bg:'info',
            },
            {
              title:'Products',
              value:products + ' products',
              icon:"package",
              color:"darkviolet",
              bg:'primary',
            },
            {
              title:'Tax',
              value:formatNumber(this.analytics.tax),
              icon:"pie-chart",
              color:"crimson",
              bg:'secondary',
            },
            {
              title:'Profit',
              value:formatNumber(this.analytics.profit),
              icon:"trending-up",
              color:"green",
              bg:'success',
              desc: 'Estimated profit'
            },
          ]
        },
      },

      watch: {
        popularityCount: async function () {
          this.popularity = await analyticsService.getProductPopularity(this.popularityCount)
        },
        profitabilityCount: async function () {
          this.profitability = await analyticsService.getProductProfitability(this.profitabilityCount)
        }
      }
    }
</script>

<style scoped>
/* Base Styles */
:root {
  --primary: #4361ee;
  --primary-light: #eef2ff;
  --success: #10b981;
  --success-light: #d1fae5;
  --info: #3b82f6;
  --info-light: #dbeafe;
  --warning: #f59e0b;
  --warning-light: #fef3c7;
  --danger: #ef4444;
  --danger-light: #fee2e2;
  --dark: #1f2937;
  --light: #f9fafb;
  --border-color: #e5e7eb;
  --text-muted: #6b7280;
}

.dashboard-container {
  padding: 1.5rem;
  max-width: 1600px;
  margin: 0 auto;
  background-color: #f8fafc;
}

/* Alerts */
.alerts-container .alert {
  border: none;
  border-radius: 10px;
  padding: 1rem 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.alerts-container .alert-danger {
  background-color: #fef2f2;
  color: #b91c1c;
}

.alerts-container .alert-warning {
  background-color: #fffbeb;
  color: #b45309;
}

.alerts-container .alert-info {
  background-color: #ecfdf5;
  color: #047857;
}

/* Header */
.dashboard-header {
  background: #fff;
  padding: 1.75rem 2rem;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  margin-bottom: 1.5rem;
}

.dashboard-header h1 {
  font-size: 1.75rem;
  font-weight: 700;
  color: #111827;
  margin: 0;
}

.date-display {
  background: #f1f5f9;
  padding: 0.5rem 1.25rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 500;
  color: #4b5563;
  white-space: nowrap;
}

/* Section Titles */
.section-title {
  font-weight: 600;
  color: #1f2937;
  position: relative;
  padding-bottom: 0.75rem;
  font-size: 1.25rem;
  margin-bottom: 1.5rem;
}

.section-title::after {
  content: '';
  position: absolute;
  left: 0;
  bottom: 0;
  width: 50px;
  height: 4px;
  background: var(--primary);
  border-radius: 2px;
}

/* Quick Actions */
.quick-actions {
  margin-bottom: 2rem;
}

.quick-action-card {
  padding: 1.5rem 1rem;
  border-radius: 14px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(0, 0, 0, 0.03);
}

.quick-action-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.05), 0 4px 6px -2px rgba(0, 0, 0, 0.03);
}

.quick-action-card .icon-wrapper {
  width: 60px;
  height: 60px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  transition: all 0.3s ease;
}

.quick-action-card:hover .icon-wrapper {
  transform: scale(1.1);
}

.quick-action-card .action-text {
  font-weight: 600;
  font-size: 1rem;
  color: #000000;
}

/* Stats Overview */
.stats-overview {
  margin-bottom: 2rem;
}

.stats-card {
  background: #fff;
  border-radius: 16px;
  padding: 1.75rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
  transition: all 0.3s ease;
  height: 100%;
  border: 1px solid rgba(0, 0, 0, 0.03);
}

.stats-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.05), 0 4px 6px -2px rgba(0, 0, 0, 0.03);
}

.stats-card .icon {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
  flex-shrink: 0;
  color: #fff;
}

.stats-card .stat-value {
  font-size: 1.75rem;
  font-weight: 700;
  line-height: 1.2;
  color: #111827;
  margin: 0.25rem 0 0.25rem;
}

.stats-card .stat-label {
  font-size: 0.9rem;
  color: #6b7280;
  margin-bottom: 0.25rem;
  font-weight: 500;
}

.stats-card .text-muted {
  font-size: 0.8rem;
  color: #9ca3af;
}

/* Product Tables */
.product-performance {
  margin-bottom: 2rem;
}

.table-card {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
  overflow: hidden;
  height: 100%;
  border: 1px solid rgba(0, 0, 0, 0.03);
  transition: all 0.3s ease;
}

.table-card:hover {
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.05), 0 4px 6px -2px rgba(0, 0, 0, 0.03);
}

.table-card .card-header {
  background: #fff;
  border-bottom: 1px solid var(--border-color);
  padding: 1.25rem 1.75rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.table-card .card-header h5 {
  font-size: 1.1rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
}

.table-card .form-select {
  border-radius: 8px;
  border-color: var(--border-color);
  font-size: 0.85rem;
  padding: 0.4rem 1.75rem 0.4rem 0.75rem;
  cursor: pointer;
}

.table-card .form-select:focus {
  border-color: var(--primary);
  box-shadow: 0 0 0 0.2rem rgba(67, 97, 238, 0.15);
}

/* Table Styles */
.table {
  margin-bottom: 0;
  font-size: 0.9rem;
}

.table th {
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.7rem;
  letter-spacing: 0.5px;
  color: #6b7280;
  border-top: none;
  padding: 1rem 1.75rem;
  background-color: #f9fafb;
  border-bottom: 1px solid var(--border-color);
}

.table td {
  padding: 1.25rem 1.75rem;
  vertical-align: middle;
  border-color: var(--border-color);
  color: #4b5563;
}

.table tbody tr {
  transition: background-color 0.2s ease;
}

.table tbody tr:hover {
  background-color: #f9fafb;
}

/* Product Rows */
.product-avatar {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background-color: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  font-weight: 600;
  font-size: 0.9rem;
}

.product-avatar .avatar-text {
  color: inherit;
}

/* Status Badges */
.badge {
  font-weight: 500;
  padding: 0.35em 0.65em;
  font-size: 0.75em;
  border-radius: 6px;
}

/* Animations */
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.fade-in {
  animation: fadeIn 0.4s ease-out forwards;
  opacity: 0;
}

/* Responsive Adjustments */
@media (max-width: 1200px) {
  .dashboard-container {
    padding: 1.25rem;
  }

  .dashboard-header {
    padding: 1.5rem;
  }

  .stats-card {
    padding: 1.5rem;
  }

  .table-card .card-header,
  .table th,
  .table td {
    padding-left: 1.25rem;
    padding-right: 1.25rem;
  }
}

@media (max-width: 768px) {
  .dashboard-container {
    padding: 1rem;
  }

  .dashboard-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
    padding: 1.25rem;
  }

  .date-display {
    align-self: flex-start;
  }

  .quick-action-card {
    padding: 1.25rem 0.5rem;
  }

  .quick-action-card .icon-wrapper {
    width: 48px;
    height: 48px;
  }

  .action-text {
    font-size: 0.9rem;
  }

  .stats-card {
    padding: 1.25rem;
  }

  .stats-card .icon {
    width: 48px;
    height: 48px;
  }

  .stats-card .stat-value {
    font-size: 1.5rem;
  }

  .table-card .card-header {
    flex-direction: column;
    gap: 0.75rem;
    align-items: flex-start;
  }

  .table-card .form-select {
    width: 100%;
    max-width: 100%;
  }
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: #6b7280;
  text-align: center;
}

/* Scrollbar Styling */
::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
  background: #a1a1a1;
}
</style>
